import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {
    useGetCatalogsQuery, useGetCompanyQuery, usePutCompanyMutation,

} from "../../store/Api";
import {
    useRunDynamicProductListingsMutation
} from "../../store/endpoints/StorefrontSettingsEndpoints";

import {
    useGetOrganizationEntitlementsQuery,
    useGetOrganizationFeatureSettingsQuery,
    useGetOrganizationQuery,
    useRebuildPurchaseStatsMutation,
    useUpdateOrganizationFeatureSettingsMutation,
    useUpdateOrganizationMutation
} from "../../store/endpoints/OrganizationsEndpoints"


import {
    CommandBar, DefaultButton, Dialog, DialogFooter, SpinButton, Text,
    Dropdown,
    Icon,
    IDropdownOption,
    Label,
    MessageBar,
    MessageBarType,
    Pivot, PivotItem,
    PrimaryButton,
    Stack,
    TooltipHost
} from "@fluentui/react";
import EditableImage from "../../components/DetailsModal/EditableImage";
import CountrySelection from "../../components/CountrySelection/CountrySelection";
import {
    IOrganization,
    IOrganizationFeatureSettings,
    IUpdateOrganizationRequest
} from "../../models/Settings";
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { IsMutationSuccess } from '../../logic/MutationTypeChecker';
import { IActionResponse } from '../../dal/BaseDAL';
import ToggleField from "../../components/Common/ToggleField";
import DataField from "../../components/Common/DataField";
import Scheduler, {ConvertCronToSchedule, ConvertScheduleToCron, ISchedule} from "../../components/Scheduler/Scheduler";
import useCan, {STOREFRONT_ADMIN} from "../../permissions/ability";
import Setting from "../../components/Common/Setting";
import ConditionalSetting from "../../components/Common/ConditionalSetting";
import {useUrlTabState} from "../../hooks/useUrlState";
import {CompanyDetailsForm} from "../../components/Contacts/CompanyDetailsModal";
import {ICompany} from "../../models/Company";

const OrganizationManagement: React.FC = () => {
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const catalogs = useGetCatalogsQuery();
    const [updateOrg] = useUpdateOrganizationMutation();
    const [saving, setSaving] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [showTaxWarning, setShowTaxWarning] = useState(false);
    
    const [schedule, setSchedule] = useState<ISchedule>();
    const [organizationFeatureSettings, setOrganizationFeatureSettings] = useState<IOrganizationFeatureSettings>({id: selectedOrg ?? ''});
    const organizationFeatures = useGetOrganizationFeatureSettingsQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    const [runNow] = useRunDynamicProductListingsMutation();
    const [processing, setProcessing] = useState(false);
    const [updateOrganizationFeatureSettings] = useUpdateOrganizationFeatureSettingsMutation();
    const [rebuildPurchaseStats, rebuildPurchaseStatsState] = useRebuildPurchaseStatsMutation();

    const defaultSchedule: ISchedule = {days: [0, 1, 2, 3, 4, 5, 6], hour: 1, minute: 0};
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('basic');


    const [organization, setOrganization] = useState<IOrganization | undefined>();
    const getOrganization = useGetOrganizationQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    
    const [saveCompany] = usePutCompanyMutation();
    const company = useGetCompanyQuery(selectedOrg ?? '', {skip: !selectedOrg|| selectedOrg === ''});
    const [workingCompany, setWorkingCompany] = React.useState<Partial<ICompany>>({});

    const getOrganizationEntitlements = useGetOrganizationEntitlementsQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''})
    useEffect(() => {
        if(getOrganization.data){
            setOrganization(getOrganization.data);
        }
    }, [getOrganization.data]);

    useEffect(() => {
        if (organizationFeatures.data) {
            setOrganizationFeatureSettings(organizationFeatures.data);
        }
    }, [organizationFeatures.data]);

    useEffect(() => {
        if (company.data) {
            setWorkingCompany(company.data);
        }
        else{
            setWorkingCompany({
                id: selectedOrg ?? '',
            });
        }
    }, [company.data]);

    useEffect(() => {
        if(organizationFeatureSettings?.dynamicProductListingUpdateSchedule){
            const schedule = ConvertCronToSchedule(organizationFeatureSettings?.dynamicProductListingUpdateSchedule)
            setSchedule(schedule)
        }
    }, [organizationFeatureSettings]);

    const save = async () => {
        if (!organization == null || !selectedOrg) return;
        setSaving(true);
        setError(undefined);
        const updatedOrg: IUpdateOrganizationRequest = {id: selectedOrg, name: organization?.name, country: organization?.country, logo: organization?.logo, defaultCatalog: organization?.defaultCatalog, punchoutCurrency: organization?.punchoutCurrency};
        const result = await updateOrg({request: updatedOrg, id: selectedOrg})
        if (IsMutationSuccess<IActionResponse>(result)) {
            if (!result.data.success) {
                setError(result.data.message ?? 'An error occurred');
            }
        } else {
            setError('An unknown error occurred');
        }
        setSaving(false);
    }
    
    const saveOrgCompanyDetails = async () => {
        if (!workingCompany || !selectedOrg) return;
        setError(undefined);
        const result = await saveCompany({...workingCompany});
        if (IsMutationSuccess<IActionResponse>(result)) {
            if (!result.data.success) {
                setError(result.data.message ?? 'An error occurred');
            }
        } else {
            setError('An unknown error occurred');
        }
    };

    const saveOrgUserSettings = async () => {
        if (!organizationFeatureSettings || !selectedOrg) return;
        setError(undefined);
        const toastId = toast.loading('Saving advanced settings…');
        const fail = (message: string) => {
            setError(message);
            toast.update(toastId, {type: 'error', render: `Failed to save advanced settings: ${message}`, isLoading: false, autoClose: 4000});
        };
        try {
            const result = await updateOrganizationFeatureSettings({request: organizationFeatureSettings, id: selectedOrg ?? ''});
            if (IsMutationSuccess<IActionResponse>(result)) {
                if (!result.data.success) {
                    fail(result.data.message ?? 'An error occurred');
                } else {
                    // Saved: nothing pending, so Save greys out and "Recalculate now" unlocks.
                    setUpdated(false);
                    toast.update(toastId, {type: 'success', render: 'Advanced settings saved', isLoading: false, autoClose: 3000});
                }
            } else {
                fail('An unknown error occurred');
            }
        } catch {
            fail('An unknown error occurred');
        }
    };
    
    const handleCurrencyChange = (currency?: string) => {
        if (organization == null) return;
        setUpdated(true);
        setOrganization({
            ...organization,
            punchoutCurrency: currency
        });
    }

    const handleNameChange = (name?: string) => {
        if (organization == null) return;
        setUpdated(true);
        setOrganization({
            ...organization,
            name: name ?? ''
        });
    }

    const handleLogoChange = (url: string) => {
        if (organization == null) return;
        setUpdated(true);
        setOrganization({
            ...organization,
            logo: url
        });
    }

    const handleCountryChange = (country: string) => {
        if (organization == null) return;
        setUpdated(true);
        setOrganization({
            ...organization,
            country
        });
    }

    const handleDefaultCatalogChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (organization == null) return;
        setUpdated(true);
        setOrganization({
            ...organization,
            defaultCatalog: option?.key as string
        });
    }
    
    const processNow = async () => {
        setProcessing(true);
        const result = await runNow("");
        if (IsMutationSuccess<IActionResponse>(result)) {
            if (!result.data.success) {
                setError(result.data.message ?? 'An error occurred');
            }
        } else {
            setError('An unknown error occurred');
        }
    }

    return (
        <div>
            <h1>Organization Management</h1>
            <CommandBar items={[]} />
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            <Pivot
                overflowBehavior='menu'
                styles={{itemContainer: {marginTop: '1em'}}}
                selectedKey={selectedTab}
                onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}
            >
                <PivotItem headerText='Basic Settings' itemKey='basic'>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <DataField
                            settingId='organization.basic.name'
                            label='Organization Name'
                            value={organization?.name}
                            onChange={(e, v) => handleNameChange(v)}
                        />
                        <div data-setting-id='organization.basic.logo'>
                            <Label>Organization Logo</Label>
                            <EditableImage
                                image={{
                                    url: organization?.logo ?? '',
                                    type: 'Custom',
                                    name: organization?.name ?? '',
                                    id: 'logo',
                                    isCustom: true,
                                }}
                                editMode={true}
                                onChange={(_, url) => handleLogoChange(url)}
                            />
                        </div>
                        <Setting id='organization.basic.country'>
                            <CountrySelection value={organization?.country} onChange={handleCountryChange}/>
                        </Setting>
                        <Setting id='organization.basic.defaultCatalog'>
                            <Dropdown
                                label={'Default Catalog'}
                                options={catalogs.data?.map((c) => ({key: c.name, text: c.description})) ?? []}
                                selectedKey={organization?.defaultCatalog}
                                onChange={handleDefaultCatalogChange}
                            />
                        </Setting>
                        <DataField
                            settingId='organization.basic.punchoutCurrency'
                            label='Punchout Currency'
                            value={organization?.punchoutCurrency}
                            onChange={(e, v) => handleCurrencyChange(v)}
                        />
                    </Stack>
                    <div style={{padding: '1em'}}>
                        <PrimaryButton
                            text={saving ? 'Saving' : 'Save'}
                            onClick={save}
                            disabled={saving || !updated}
                        />
                    </div>
                </PivotItem>
                {getOrganizationEntitlements.data?.procurementEnabled && organization?.id &&
                <PivotItem headerText='Address' itemKey='address'>
                    <Stack>
                        <div>
                            <CompanyDetailsForm
                                company={organization?.id}
                                onChange={(updates) => {
                                    setWorkingCompany((current) => ({...current, ...updates}))
                                    setUpdated(true)
                                }}
                            />
                        </div>
                        <div style={{padding: '1em'}}>
                            <PrimaryButton
                                text={saving ? 'Saving' : 'Save'}
                                onClick={saveOrgCompanyDetails}
                                disabled={saving || !updated}
                            />
                        </div>
                    </Stack>
                </PivotItem>}
                {canStorefrontAdmin &&
                <PivotItem headerText={'Advanced Settings'} itemKey='advanced'>
                    <Stack tokens={{childrenGap: '1em'}}>
                         <div>
                            <h3>Dynamic Product Listings</h3>

                            <ToggleField
                                settingId='organization.advanced.enableDynamicProductListings'
                                inline={true}
                                label='Use Dynamic Product Listings'
                                checked={organizationFeatureSettings?.enableDynamicProductListings}
                                onChange={(_, v) => {
                                    setOrganizationFeatureSettings((settings) => ({ ...settings, enableDynamicProductListings: v }));
                                    setUpdated(true);
                                }}
                            />
                            <ConditionalSetting when={!!organizationFeatureSettings?.enableDynamicProductListings} id='organization.advanced.dynamicProductListingSchedule'>
                                <div data-setting-id='organization.advanced.dynamicProductListingSchedule'>
                                    <TooltipHost content="Dynamic product listings are updated based on the schedule set below. Please note, only products that have been resolved within the last 24 hours will be included in listing results on Storefront.">
                                        <div style={{display: "inline-flex"}}><Label><Icon iconName={'Info'} style={{paddingRight: '5px'}}/>Dynamic Product Update Schedule</Label> </div>
                                    </TooltipHost>

                                    <div style={{ width: '250px' }}>
                                        <DefaultButton
                                            disabled={processing}
                                            text={'Run Now'}
                                            onClick={processNow}
                                        />
                                        <Scheduler schedule={schedule ?? defaultSchedule} onChange={(value) => {
                                            setOrganizationFeatureSettings((settings) => ({...settings, dynamicProductListingUpdateSchedule: ConvertScheduleToCron(value)}))
                                            setSchedule(value);
                                            setUpdated(true);
                                        }} />
                                    </div>

                                </div>
                            </ConditionalSetting>
                             <h3>Purchase History</h3>
                             <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {maxWidth: 520}}}>
                                 <SpinButton
                                     label='Count purchases from the last (months)'
                                     title='How far back the product "Ordered" counters and the "Sold Before" search filter look. 0 counts all time.'
                                     min={0}
                                     max={120}
                                     step={1}
                                     value={String(organizationFeatureSettings?.purchaseStatsMonths ?? 0)}
                                     onChange={(_, v) => {
                                         const months = Math.max(0, Math.min(120, parseInt(v ?? '0', 10) || 0));
                                         setOrganizationFeatureSettings((settings) => ({...settings, purchaseStatsMonths: months}));
                                         setUpdated(true);
                                     }}
                                 />
                                 <Text variant='small' styles={{root: {color: '#605e5c'}}}>
                                     {(organizationFeatureSettings?.purchaseStatsMonths ?? 0) > 0
                                         ? `The Ordered column and Sold Before filter count storefront orders and imported purchase history from the last ${organizationFeatureSettings?.purchaseStatsMonths} months. Recalculated nightly.`
                                         : 'The Ordered column and Sold Before filter count all storefront orders and imported purchase history. Recalculated nightly.'}
                                 </Text>
                                 <div>
                                     <DefaultButton
                                         text={rebuildPurchaseStatsState.isLoading ? 'Queuing…' : 'Recalculate now'}
                                         iconProps={{iconName: 'Refresh'}}
                                         disabled={rebuildPurchaseStatsState.isLoading || updated}
                                         title={updated ? 'Save first so the recalculation uses the new window' : 'Recompute every product\'s purchase counters now'}
                                         onClick={async () => {
                                             const toastId = toast.loading('Queuing purchase statistics recalculation…');
                                             try {
                                                 const result = await rebuildPurchaseStats().unwrap();
                                                 toast.update(toastId, {type: result.success ? 'success' : 'error', render: result.message ?? 'Purchase statistics recalculation queued', isLoading: false, autoClose: 4000});
                                             } catch {
                                                 toast.update(toastId, {type: 'error', render: 'Failed to queue the purchase statistics recalculation', isLoading: false, autoClose: 4000});
                                             }
                                         }}
                                     />
                                 </div>
                             </Stack>
                             <h3>Taxes</h3>
                             <ToggleField
                                 settingId='organization.advanced.enableSimpleTaxCalculation'
                                 inline={true}
                                 label='Use Simple Tax Calculation For Storefront'
                                 checked={organizationFeatureSettings?.enableSimpleTaxCalculation}
                                 onChange={(_, v) => {
                                     if(v){
                                        setShowTaxWarning(true);
                                     }
                                     else{
                                         setOrganizationFeatureSettings((settings) => ({...settings, enableSimpleTaxCalculation: false}))
                                         setUpdated(true);
                                     }
                                 }}
                             />

                        </div>
                        <div style={{padding: '1em'}}>
                            <PrimaryButton
                                text={saving ? 'Saving' : 'Save'}
                                onClick={saveOrgUserSettings}
                                disabled={saving || !updated}
                            />
                        </div>
                    </Stack>
                </PivotItem>}
            </Pivot>
            <Dialog
                hidden={!showTaxWarning}
                modalProps={{
                    isBlocking: true,
                    onDismiss:() => {
                        setShowTaxWarning(false)
                    }
                }}
                dialogContentProps={{
                    title: 'Tax Calculation Warning',
                    subText: `The Simple Tax feature provides a single, flat tax rate per storefront and does not adjust based on customer location, product type, or shipping address. This estimate is for convenience only and may not reflect actual tax liability. Always consult a qualified tax professional before relying on this feature for compliance or reporting purposes.
                    Are you sure you want to enable simple tax calculation?`,
                }}
            >
                <DialogFooter>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <PrimaryButton text='Yes, Enable' onClick={() => {
                            setOrganizationFeatureSettings((settings) => ({...settings, enableSimpleTaxCalculation: true}))
                            setUpdated(true);
                            setShowTaxWarning(false);
                        }} />
                        <DefaultButton text='Cancel' onClick={() => {
                            setShowTaxWarning(false);
                        }} />
                    </Stack>
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default OrganizationManagement;